'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { TravelAgencyTour } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';

import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { TourSlideImage } from './tour-slide-image';
import { getTourPhotos, toTourLightboxImages } from '../../../utils/tour-media';

export function TourImages({ tour }: { tour: TravelAgencyTour }) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = getTourPhotos(tour);
  if (!allImages.length) return null;

  return (
    <>
      <LightboxComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        images={imagesSorted}
        zoom={{ maxZoomPixelRatio: 3, zoomInMultiplier: 2, doubleTapDelay: 300 }}
      />
      <div className="hidden grid-cols-3 gap-4 md:grid md:max-w-[760px]">
        <Image
          onClick={handleOpenGallery(toTourLightboxImages(allImages), 0)}
          src={allImages[0]?.url}
          alt={resolveImageAlt(allImages?.[0], 'Tour')}
          height={0}
          width={0}
          className="col-span-3 h-[350px] w-full rounded-[24px] object-cover object-center sm:h-[428px]"
          sizes="(min-width: 768px) 760px, 100vw"
          loading="eager"
          fetchPriority="high"
        />
        {allImages.slice(1, 4).map((image, index) => (
          <div
            onClick={handleOpenGallery(toTourLightboxImages(allImages), index + 1)}
            key={index}
            className="relative col-span-1 h-[80px] w-full overflow-hidden rounded-[24px] lg:h-[100px] xl:h-[136px]"
          >
            <Image
              src={image.url}
              alt={resolveImageAlt(image, 'Tour')}
              height={0}
              width={0}
              className="relative z-0 h-full w-full object-cover object-center"
              sizes="50vw"
            />
            {index === 2 && allImages.length > 4 && (
              <div className="absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-black/50">
                <span className="text-base font-normal text-white">
                  +{allImages.length - 4} fotografie
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      <Carousel
        showDots={true}
        className={{
          container: 'w-full',
          main: 'w-full',
          slide: 'h-[220px] w-full sm:h-[340px]',
          wrapper: 'flex w-full md:hidden',
        }}
        slides={allImages.map((image, index) => {
          const photo = (
            <Image
              key={index}
              src={image?.url ?? ''}
              alt={resolveImageAlt(image, 'Tour')}
              height={0}
              width={0}
              className="h-[220px] w-full object-cover object-center sm:h-[340px]"
              sizes="(min-width: 768px) 760px, 100vw"
              loading="eager"
              fetchPriority={index === 0 ? 'high' : 'auto'}
            />
          );
          return index === 0 ? photo : <TourSlideImage key={index}>{photo}</TourSlideImage>;
        })}
      />
    </>
  );
}
